import React from "react";
import { HtmlTooltip, IconContainer, SpaceBetweenStack, StyledMainBox } from "../../components/commonComponents/StyledComponents/styled";
import AccessControl from "../../components/commonComponents/Hoc/AccessControl";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Stack, Typography } from "@mui/material";
import DateInputField from "../../components/commonComponents/TextInputs/DateInputs";
import { clampDate, standardFormatToday, reformatDateToUserFormat } from "../../api_calls/utils";
import DetailedStoreFilter from "../../components/commonComponents/DetailedStore/DetailedStoreFilter";
import { deleteItems, updateStateData } from "../../Store/actions";
import ActionButton, { TextButton } from "../../components/commonComponents/Buttons/ActionButton";
import { DisabledGreyButton } from "../../components/commonComponents/Buttons/DisabledButton";
import Dashboard from "../../components/commonComponents/Embeddable/Dashboard";
import { checkUserDetailsFromCookie } from "../../api_calls/userManagement";
import { Download, Info } from "@mui/icons-material";
import { fetchDownloadableData } from "../../api_calls/advertisingCapacity";

const AdvertisingCapacity = () => {
    const userStateDetails = useSelector((state) => state.userSelections);
    const dispatchAction = useDispatch();
    const [showDashboard, setShowDashboard] = React.useState(false);
    const [userInfo, setUserInfo] = React.useState({});
    const [selectedStartDate, setSelectedStartDate] = React.useState("");
    const [selectedEndDate, setSelectedEndDate] = React.useState("");
    const [fieldErrors, setFieldErrors] = React.useState({ start_date: "", end_date: "" });

    // Returns the upper bound for the end date:
    // the earlier of (start date + 12 weeks) and (today + 6 months)
    const getEndDateMax = (startDate) => {
        const sixMonthsFromToday = new Date();
        sixMonthsFromToday.setMonth(sixMonthsFromToday.getMonth() + 6);

        if (!startDate) {
            return sixMonthsFromToday;
        }

        const twelveWeeksFromStart = new Date(startDate);
        twelveWeeksFromStart.setDate(twelveWeeksFromStart.getDate() + 12 * 7);

        return twelveWeeksFromStart < sixMonthsFromToday ? twelveWeeksFromStart : sixMonthsFromToday;
    };

    const validateDates = (startDate, endDate) => {
        const errors = { start_date: "", end_date: "" };
        const today = standardFormatToday();

        if (startDate !== "" && startDate < today) {
            errors.start_date = "Start date cannot be in the past";
        }

        if (endDate !== "") {
            if (startDate !== "" && endDate < startDate) {
                errors.end_date = "End date cannot be before start date";
            } else if (startDate !== "" && new Date(endDate) > getEndDateMax(startDate)) {
                errors.end_date = "End date cannot exceed 12 weeks from start date";
            } else {
                const sixMonthsFromToday = new Date();
                sixMonthsFromToday.setMonth(sixMonthsFromToday.getMonth() + 6);
                if (new Date(endDate) > sixMonthsFromToday) {
                    errors.end_date = "End date cannot exceed 6 months from today";
                }
            }
        }

        return errors;
    };

    // Week selection handler - sets both start and end date
    const handleWeekSelect = (monday, sunday, fieldRole) => {
        const today = standardFormatToday();

        const startFieldMin = today;
        const clampedStart = clampDate(monday, startFieldMin, null);

        const endFieldMin = clampedStart >= today ? clampedStart : today;
        const endFieldMax = getEndDateMax(clampedStart);
        const clampedEnd = clampDate(sunday, endFieldMin, endFieldMax);

        if (clampedStart > clampedEnd) {
            return;
        }

        const updateData = {
            end_date: clampedEnd,
            start_date: clampedStart,
        };

        if (Object.keys(updateData).length > 0) {
            const newStart = updateData.start_date || selectedStartDate;
            const newEnd = updateData.end_date || selectedEndDate;
            setSelectedStartDate(newStart);
            setSelectedEndDate(newEnd);
            setFieldErrors(validateDates(newStart, newEnd));
        }
    };

    const setStartDateConstraint = () => {
        return standardFormatToday();
    };

    const toUtcMidnight = (localDate) => {
        return new Date(Date.UTC(localDate.getFullYear(), localDate.getMonth(), localDate.getDate()));
    };

    const dashboardVariables = () => {
        let capacityDashboardVariables = {};
        // add stores
        if (typeof userStateDetails.advertising_capacity_selected_stores !== "undefined" && userStateDetails.advertising_capacity_selected_stores.length !== 0) {
            capacityDashboardVariables = {
                ...capacityDashboardVariables,
                stores: userStateDetails.advertising_capacity_selected_stores,
            }
        }
        // start/end date
        if (typeof userStateDetails.advertising_capacity_selected_dates !== "undefined" && typeof userStateDetails.advertising_capacity_selected_dates.from !== "undefined" && typeof userStateDetails.advertising_capacity_selected_dates.to !== "undefined") {
            const fromLocal = new Date(userStateDetails.advertising_capacity_selected_dates.from);
            const toLocal = new Date(userStateDetails.advertising_capacity_selected_dates.to);

            const fromDate = toUtcMidnight(fromLocal);
            const toDate = new Date(Date.UTC(toLocal.getFullYear(), toLocal.getMonth(), toLocal.getDate() + 1));

            capacityDashboardVariables = {
                ...capacityDashboardVariables,
                Date: {
                    from: fromDate,
                    to: toDate,
                },
            }
        }

        return capacityDashboardVariables;
    }

    const saveSelectedDataToRedux = () => {
        console.log("Saving to Redux:", {
            from: selectedStartDate,
            to: selectedEndDate,
        });
        dispatchAction(updateStateData({
            advertising_capacity_selected_dates: {
                from: selectedStartDate,
                to: selectedEndDate,
            },
            advertising_capacity_selected_stores: userStateDetails.advertising_capacity_stores.store_ids,
        }))
    }

    React.useEffect(() => {
        const checkUserDetails = checkUserDetailsFromCookie();
        let userDetailObj = {};
        if (checkUserDetails.success === true) {
            userDetailObj = checkUserDetails.user_detail;
        }
        setUserInfo(userDetailObj);

        // Resetting store and dates when the component unmounts
        return () => {
            dispatchAction(deleteItems(["advertising_capacity_selected_dates", "advertising_capacity_stores", "store_list_filter_applied", "detailed_stores_filtered"]));
        };
    }, []);

    const hasValidationErrors = fieldErrors.start_date !== "" || fieldErrors.end_date !== "";
    const isCheckReady =
        selectedStartDate !== "" &&
        selectedEndDate !== "" &&
        !hasValidationErrors &&
        typeof userStateDetails.advertising_capacity_stores !== "undefined" &&
        userStateDetails.advertising_capacity_stores.store_ids.length > 0;

    return (
        <StyledMainBox component="main">
            <Box height={20} />
            <Stack direction="row" alignItems="center" spacing={1}>
                <h1>Capacity Planning</h1>
                <HtmlTooltip
                    title={
                        <Box sx={{ maxWidth: 450 }}>
                            <Typography variant="body2">
                                The first and last calendar weeks only contain the weekdays included in the date range selected above. Click any row to see more details about it. Only enabled campaigns are included in the simulated capacity.
                            </Typography>
                            <Box height={10} />
                            <Typography variant="body2">
                                The results represent a snapshot of the current situation, as scheduled campaigns and forecast impressions may change over time. Forecast data is based on historical data patterns and may not always be perfectly accurate.
                            </Typography>
                        </Box>
                    }
                >
                    <IconContainer>
                        <Info fontSize="small" sx={{ marginTop: 1, color: "#AF3241" }} />
                    </IconContainer>
                </HtmlTooltip>
            </Stack>
            <Box height={10} />
            <Typography variant="body2">
                Perform a capacity check for contact-based advertising. The check uses forecast impression data. Checks are performed for selected stores
            </Typography>
            <Box height={10} />
            <Typography variant="body2">
                Select the date range to verify availability. A maximum of 12 weeks can be checked at once.
            </Typography>
            <Box height={20} />
            <Box width={"50%"}>
                <Stack direction={"row"} spacing={2}>
                    {/* ------ start date -------- */}
                    <DateInputField
                        fieldRequired={true}
                        elemId="advertisingStartDate"
                        label="Start Date"
                        fieldName="advertising_start_date"
                        className="start_date_class"
                        defaultVal={selectedStartDate}
                        showlabel={false}
                        inputType={"date"}
                        minSelectable={setStartDateConstraint()}
                        maxSelectable={selectedEndDate !== "" ? selectedEndDate : getEndDateMax(null)}
                        onChangeCall={(selectedDate) => {
                            setSelectedStartDate(selectedDate);
                            // Clear end date if it falls outside the new valid range
                            const newEndDate = selectedEndDate !== "" && (selectedEndDate < selectedDate || new Date(selectedEndDate) > getEndDateMax(selectedDate))
                                ? ""
                                : selectedEndDate;
                            if (newEndDate !== selectedEndDate) {
                                setSelectedEndDate(newEndDate);
                            }
                            setFieldErrors(validateDates(selectedDate, newEndDate));
                        }}
                        onWeekSelect={handleWeekSelect}
                        fieldError={fieldErrors.start_date}
                    />
                    {/* -------- end date ------- */}
                    <DateInputField
                        fieldRequired={true}
                        elemId="advertisingEndDate"
                        label="End Date"
                        fieldName="advertising_end_date"
                        className="end_date_class"
                        defaultVal={selectedEndDate}
                        showlabel={false}
                        inputType={"date"}
                        disabled={selectedStartDate === ""}
                        minSelectable={selectedStartDate}
                        maxSelectable={getEndDateMax(selectedStartDate)}
                        onChangeCall={(selectedDate) => {
                            setSelectedEndDate(selectedDate);
                            setFieldErrors(validateDates(selectedStartDate, selectedDate));
                        }}
                        onWeekSelect={handleWeekSelect}
                        fieldError={fieldErrors.end_date}
                    />
                </Stack>
                <Box height={10} />
                <Box width={"49%"}>
                    <DetailedStoreFilter reduxKeywordToSaveValue={"advertising_capacity_stores"} />
                </Box>
            </Box>
            <Box height={15} />
            <Box>
                <SpaceBetweenStack
                    direction={"row"}
                    spacing={2}
                    width={"100%"}
                    justifyContent={"space-between"}
                    display={"flex"}
                >
                    {isCheckReady ? (
                        <ActionButton
                            onClick={(e) => {
                                e.preventDefault();
                                saveSelectedDataToRedux();
                                setTimeout(() => {
                                    setShowDashboard(true);
                                }, 500);
                            }}
                            label={"Check"}
                            icon={<></>}
                        />
                    ) : (
                        <DisabledGreyButton
                            onClick={(e) => {
                                e.preventDefault();
                            }}
                            label="Check"
                            icon={<></>}
                            toolTipContent="Not available"
                        />
                    )}
                </SpaceBetweenStack>
            </Box>

            <Box height={10} />
            {/* -------- sub heading ---- DASHBOARD SECTION --------- */}
            {typeof userInfo.root_company_id !== "undefined" && showDashboard === true && (
                <Box width={"100%"}>
                    <SpaceBetweenStack
                        direction={"row"}
                        spacing={2}
                        width={"100%"}
                        justifyContent={"space-between"}
                        display={"flex"}
                    >
                        <Stack direction="row" alignItems="center" spacing={1}>
                            {/* <h2>Remaining Capacity</h2> */}
                            {/* <HtmlTooltip
                                title={
                                    <Box sx={{ maxWidth: 450 }}>
                                        <Typography variant="body2">
                                            The first and last calendar weeks only contain the weekdays included in the date range selected above. Click any row to see more details about it. Only enabled campaigns are included in the simulated capacity.
                                        </Typography>
                                        <Box height={10} />
                                        <Typography variant="body2">
                                            The results represent a snapshot of the current situation, as scheduled campaigns and forecast impressions may change over time. Forecast data is based on historical data patterns and may not always be perfectly accurate.
                                        </Typography>
                                    </Box>
                                }
                            >
                                <IconContainer>
                                    <Info fontSize="small" sx={{ marginTop: 1, color: "#AF3241" }} />
                                </IconContainer>
                            </HtmlTooltip> */}
                        </Stack>

                        <Box sx={{ position: "relative", display: "inline-block" }}>
                            <TextButton
                                onClick={(e) => {
                                    e.preventDefault();
                                    fetchDownloadableData(dashboardVariables(), userInfo.root_company_id, "7d630654-b271-4697-bd96-6c1a993d19a2");
                                }}
                                label={"Download Excel"}
                                icon={<Download />}
                            />
                        </Box>
                    </SpaceBetweenStack>
                </Box>
            )}
            {/* -------- embeddable dashboard ---------- */}
            <Grid container spacing={2}>
                {typeof userInfo.root_company_id !== "undefined" && showDashboard === true && (
                    <Dashboard
                        embedDashboardId={"7d630654-b271-4697-bd96-6c1a993d19a2"}
                        variables={dashboardVariables()}
                        filterConfig={{
                            "number_root": userInfo.root_company_id.toString(),
                        }} />
                )}
            </Grid>
        </StyledMainBox>
    );
};

export default AccessControl(AdvertisingCapacity, ["advertising_capacity_can_view"], [], ["capacityCheck"]);
